import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function Services() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeCategory, setActiveCategory] = useState(0);

  const categories = [
    {
      name: 'Arquitectura Técnica',
      icon: 'ri-building-line',
      services: [
        'Topic Clusters Semánticos',
        'Schema Markup Avanzado (Organization, WebSite, Product, etc.)',
        'Enlaces Internos Inteligentes con Link Juice',
        'Progressive Web App (PWA) Completa',
        'Breadcrumbs con Schema',
        'Canonical Tags Dinámicos',
        'Hreflang Tags Multiidioma',
        'Sitemap XML Dinámico',
        'Robots.txt Avanzado'
      ],
      color: 'cyan-500'
    },
    {
      name: 'Optimización para IA',
      icon: 'ri-brain-line',
      services: [
        'SEO para ChatGPT, Claude y Gemini',
        'Contenido Estructurado para Agentes IA',
        'Voice Search Optimization',
        'Consultas Conversacionales',
        'Featured Snippets (Posición Cero)',
        'QAPage Schema para Preguntas',
        'Respuestas Directas Optimizadas',
        'Metadatos para Asistentes de Voz'
      ],
      color: 'purple-500'
    },
    {
      name: 'Performance Extrema',
      icon: 'ri-rocket-line',
      services: [
        'Core Web Vitals Optimizados (LCP, FID, CLS)',
        'Lazy Loading Inteligente',
        'Precarga de Recursos Críticos',
        'Prefetch de Páginas Probables',
        'Sistema de Caché Avanzado',
        'Optimización Critical Rendering Path',
        'Font Display Swap',
        'Resource Hints (dns-prefetch, preconnect)',
        'Image Optimization Automática'
      ],
      color: 'green-500'
    },
    {
      name: 'Contenido Estructurado',
      icon: 'ri-file-code-line',
      services: [
        'HTML Semántico Estricto',
        'Jerarquía H1-H6 Perfecta',
        'Atributos ARIA para Accesibilidad',
        'Microdata y Schema.org',
        'Open Graph Completo (Facebook)',
        'Twitter Cards Optimizadas',
        'Metadatos Sociales',
        'Structured Data Testing',
        'Internal Link Suggestions'
      ],
      color: 'orange-500'
    }
  ];

  return (
    <section id="servicios" ref={ref} className="py-16 sm:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4 px-4">
            Servicios <span className="text-cyan-500">SEO Completos</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl px-4">
            Implementamos todos estos servicios en tu web como ejemplo de trabajo bien hecho
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Categories List */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-1 space-y-3 sm:space-y-4"
          >
            {categories.map((category, index) => (
              <button
                key={index}
                onClick={() => setActiveCategory(index)}
                className={`w-full text-left p-4 sm:p-6 rounded-2xl transition-all duration-300 border-2 ${
                  activeCategory === index
                    ? 'bg-white shadow-xl border-transparent'
                    : 'bg-white/50 border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${
                    activeCategory === index
                      ? `bg-${category.color}`
                      : 'bg-gray-100'
                  }`}>
                    <i className={`${category.icon} text-xl sm:text-2xl ${
                      activeCategory === index ? 'text-white' : 'text-gray-400'
                    }`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-bold text-base sm:text-lg ${
                      activeCategory === index ? 'text-gray-800' : 'text-gray-600'
                    }`}>
                      {category.name}
                    </h3>
                    <div className={`text-xs sm:text-sm ${
                      activeCategory === index
                        ? `text-${category.color} font-medium`
                        : 'text-gray-400'
                    }`}>
                      {category.services.length} servicios
                    </div>
                  </div>
                  {activeCategory === index && (
                    <i className={`ri-arrow-right-line text-xl sm:text-2xl text-${category.color} hidden sm:block`}></i>
                  )}
                </div>
              </button>
            ))}
          </motion.div>

          {/* Services Display */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-gray-100">
              <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center bg-${categories[activeCategory].color}`}>
                  <i className={`${categories[activeCategory].icon} text-2xl sm:text-3xl text-white`}></i>
                </div>
                <div className="min-w-0">
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 truncate">{categories[activeCategory].name}</h3>
                  <p className="text-sm sm:text-base text-gray-600">Servicios implementados en tu web</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                {categories[activeCategory].services.map((service, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl bg-gray-50 hover:shadow-md transition-all duration-300 border border-gray-100"
                  >
                    <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-lg flex items-center justify-center bg-${categories[activeCategory].color} flex-shrink-0 mt-0.5`}>
                      <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                    </div>
                    <span className="text-xs sm:text-sm text-gray-700 leading-relaxed">{service}</span>
                  </motion.div>
                ))}
              </div>

              {/* Code Preview */}
              <div className="mt-6 sm:mt-8 bg-gray-900 rounded-2xl p-4 sm:p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 sm:w-64 sm:h-64 bg-cyan-500/10 rounded-full blur-3xl"></div>
                <div className="relative">
                  <div className="flex items-center gap-2 mb-3 sm:mb-4">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-red-500"></div>
                    <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-green-500"></div>
                    <span className="ml-2 sm:ml-4 text-gray-400 text-xs sm:text-sm">Ejemplo de implementación</span>
                  </div>
                  <pre className="text-xs sm:text-sm text-gray-300 overflow-x-auto">
                    <code>{`<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "SEOgenia",
  "url": "https://seogenia.es",
  "logo": "https://seogenia.es/logo.png"
}
</script>`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Additional Services Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 sm:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
        >
          {[
            { icon: 'ri-shield-check-line', title: 'Seguridad Formularios', color: 'cyan-500' },
            { icon: 'ri-message-3-line', title: 'Chatbot Integrado', color: 'blue-500' },
            { icon: 'ri-cookie-line', title: 'Política Cookies GDPR', color: 'purple-500' },
            { icon: 'ri-map-pin-line', title: 'Geo-localización SEO', color: 'orange-500' }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.8 + index * 0.1 }}
              className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group hover:-translate-y-1"
            >
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-${item.color} mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <i className={`${item.icon} text-white text-lg sm:text-2xl`}></i>
              </div>
              <h4 className="font-bold text-sm sm:text-base text-gray-800">{item.title}</h4>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
